import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export type ReportTab = 
  | 'account' 
  | 'ds-general' 
  | 'ds-specific' 
  | 'custodian-services' 
  | 'inst-user' 
  | 'inst-services' 
  | 'inst-admin-ds';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header Section -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 class="text-xl font-extrabold text-slate-900 flex items-center gap-2">
            <svg class="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Governance & Audit Reports
          </h1>
          <p class="text-xs text-slate-500 mt-1">
            Comprehensive auditability, system throughput, service availability, and compliance metrics across ZAN-X Change.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <button (click)="exportReport('csv')" class="px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 flex items-center gap-2 transition">
            <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
          <button (click)="exportReport('pdf')" class="px-3.5 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm flex items-center gap-2 transition">
            <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print / PDF
          </button>
        </div>
      </div>

      <!-- Report Tabs Bar -->
      <div class="flex items-center gap-1 p-1.5 bg-white border border-slate-200 rounded-2xl overflow-x-auto shadow-xs">
        <button
          *ngFor="let tab of tabs"
          (click)="activeTab.set(tab.id)"
          [ngClass]="activeTab() === tab.id 
            ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold shadow-2xs' 
            : 'text-slate-500 hover:text-slate-900 border-transparent hover:bg-slate-50'"
          class="px-3.5 py-2 rounded-xl text-xs whitespace-nowrap border transition-all flex items-center gap-2"
        >
          <span>{{ tab.icon }}</span>
          {{ tab.label }}
        </button>
      </div>

      <!-- Filter Controls Bar -->
      <div class="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex flex-wrap items-center gap-4 text-xs">
        <div class="flex items-center gap-2">
          <label class="text-slate-600 font-bold">Institution:</label>
          <select [(ngModel)]="selectedInstitution" class="bg-white text-slate-900 border border-slate-300 rounded-xl px-3 py-1.5 focus:border-emerald-500 focus:outline-none">
            <option value="all">All Institutions</option>
            <option value="mofez">Ministry of Finance (MoFEZ)</option>
            <option value="mohz">Ministry of Health (MoHZ)</option>
            <option value="zra">Zanzibar Revenue Authority (ZRA)</option>
            <option value="zanid">ZanID Registration Agency</option>
          </select>
        </div>

        <div class="flex items-center gap-2">
          <label class="text-slate-600 font-bold">From:</label>
          <input type="date" [(ngModel)]="startDate" class="bg-white text-slate-900 border border-slate-300 rounded-xl px-3 py-1.5 focus:border-emerald-500 focus:outline-none" />
        </div>

        <div class="flex items-center gap-2">
          <label class="text-slate-600 font-bold">To:</label>
          <input type="date" [(ngModel)]="endDate" class="bg-white text-slate-900 border border-slate-300 rounded-xl px-3 py-1.5 focus:border-emerald-500 focus:outline-none" />
        </div>

        <div class="flex items-center gap-2 ml-auto">
          <span class="text-slate-500">Filtered Records:</span>
          <span class="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-300 font-bold">
            {{ getFilteredCount() }} Records
          </span>
        </div>
      </div>

      <!-- TAB 1: Account Report -->
      <div *ngIf="activeTab() === 'account'" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div class="p-4 rounded-xl bg-white border border-slate-200 shadow-xs">
            <div class="text-slate-500 text-xs font-bold uppercase">Total Portal Users</div>
            <div class="text-2xl font-black text-slate-900 mt-1">1,248</div>
            <div class="text-[10px] text-emerald-700 font-bold mt-1">↑ 12% vs last month</div>
          </div>
          <div class="p-4 rounded-xl bg-white border border-slate-200 shadow-xs">
            <div class="text-slate-500 text-xs font-bold uppercase">Active Institutional Admins</div>
            <div class="text-2xl font-black text-emerald-700 mt-1">142</div>
            <div class="text-[10px] text-slate-500 font-medium mt-1">Across 38 Ministry Nodes</div>
          </div>
          <div class="p-4 rounded-xl bg-white border border-slate-200 shadow-xs">
            <div class="text-slate-500 text-xs font-bold uppercase">Data Protection Officers</div>
            <div class="text-2xl font-black text-purple-700 mt-1">64</div>
            <div class="text-[10px] text-slate-500 font-medium mt-1">Certified e-Gov DPOs</div>
          </div>
          <div class="p-4 rounded-xl bg-white border border-slate-200 shadow-xs">
            <div class="text-slate-500 text-xs font-bold uppercase">Suspended Accounts</div>
            <div class="text-2xl font-black text-rose-700 mt-1">3</div>
            <div class="text-[10px] text-rose-600 font-bold mt-1">Security Audit Flagged</div>
          </div>
        </div>

        <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table class="w-full text-left text-xs text-slate-700">
            <thead class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
              <tr>
                <th class="p-3.5">User Details</th>
                <th class="p-3.5">Institution</th>
                <th class="p-3.5">Role</th>
                <th class="p-3.5">Last Active</th>
                <th class="p-3.5">Status</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              <tr *ngFor="let u of accountReportData" class="hover:bg-emerald-50/30 transition">
                <td class="p-3.5 font-bold text-slate-900">{{ u.name }} <div class="text-[10px] font-normal text-slate-500">{{ u.email }}</div></td>
                <td class="p-3.5 text-slate-700">{{ u.institution }}</td>
                <td class="p-3.5"><span class="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-bold">{{ u.role }}</span></td>
                <td class="p-3.5 text-slate-500 font-medium">{{ u.lastActive }}</td>
                <td class="p-3.5">
                  <span class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TAB 2: General Data Sharing Report -->
      <div *ngIf="activeTab() === 'ds-general'" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div class="p-4 rounded-xl bg-white border border-slate-200 shadow-xs">
            <div class="text-slate-500 text-xs font-bold uppercase">Total Requests Handled</div>
            <div class="text-2xl font-black text-slate-900 mt-1">482</div>
          </div>
          <div class="p-4 rounded-xl bg-white border border-slate-200 shadow-xs">
            <div class="text-slate-500 text-xs font-bold uppercase">Approved DSAs</div>
            <div class="text-2xl font-black text-emerald-700 mt-1">390</div>
          </div>
          <div class="p-4 rounded-xl bg-white border border-slate-200 shadow-xs">
            <div class="text-slate-500 text-xs font-bold uppercase">Pending Review</div>
            <div class="text-2xl font-black text-amber-700 mt-1">68</div>
          </div>
          <div class="p-4 rounded-xl bg-white border border-slate-200 shadow-xs">
            <div class="text-slate-500 text-xs font-bold uppercase">Avg Approval SLA</div>
            <div class="text-2xl font-black text-sky-700 mt-1">2.4 Days</div>
          </div>
        </div>

        <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table class="w-full text-left text-xs text-slate-700">
            <thead class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
              <tr>
                <th class="p-3.5">Category</th>
                <th class="p-3.5">Consumer Entity</th>
                <th class="p-3.5">Provider Entity</th>
                <th class="p-3.5">Submitted Date</th>
                <th class="p-3.5">Status</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              <tr *ngFor="let r of dsGeneralData" class="hover:bg-emerald-50/30 transition">
                <td class="p-3.5 font-bold text-slate-900">{{ r.category }}</td>
                <td class="p-3.5 text-slate-700">{{ r.consumer }}</td>
                <td class="p-3.5 text-emerald-800 font-bold">{{ r.provider }}</td>
                <td class="p-3.5 text-slate-500">{{ r.date }}</td>
                <td class="p-3.5">
                  <span [ngClass]="{
                    'bg-emerald-50 text-emerald-700 border-emerald-200': r.status === 'Approved',
                    'bg-amber-50 text-amber-700 border-amber-200': r.status === 'Under Review',
                    'bg-rose-50 text-rose-700 border-rose-200': r.status === 'Rejected'
                  }" class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border">
                    {{ r.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TAB 3: Specific Data Sharing Report -->
      <div *ngIf="activeTab() === 'ds-specific'" class="space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table class="w-full text-left text-xs text-slate-700">
            <thead class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
              <tr>
                <th class="p-3.5">Agreement Reference</th>
                <th class="p-3.5">Service Endpoint</th>
                <th class="p-3.5">Requested Datasets / Scopes</th>
                <th class="p-3.5">Legal Basis</th>
                <th class="p-3.5">Expiry Date</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              <tr *ngFor="let s of dsSpecificData" class="hover:bg-emerald-50/30 transition">
                <td class="p-3.5 font-mono text-emerald-700 font-bold">{{ s.ref }}</td>
                <td class="p-3.5 font-bold text-slate-900">{{ s.service }}</td>
                <td class="p-3.5 text-slate-700">
                  <span *ngFor="let field of s.fields" class="inline-block mr-1 my-0.5 px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-mono font-bold">
                    {{ field }}
                  </span>
                </td>
                <td class="p-3.5 text-slate-600 italic font-medium">{{ s.legalBasis }}</td>
                <td class="p-3.5 text-slate-700 font-medium">{{ s.expiry }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TAB 4: Custodian Services Report -->
      <div *ngIf="activeTab() === 'custodian-services'" class="space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table class="w-full text-left text-xs text-slate-700">
            <thead class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
              <tr>
                <th class="p-3.5">Custodian Institution</th>
                <th class="p-3.5">Published API Services</th>
                <th class="p-3.5">Total API Calls (30d)</th>
                <th class="p-3.5">System Uptime SLA</th>
                <th class="p-3.5">Status</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              <tr *ngFor="let c of custodianServicesData" class="hover:bg-emerald-50/30 transition">
                <td class="p-3.5 font-bold text-slate-900">{{ c.custodian }}</td>
                <td class="p-3.5 text-emerald-800 font-bold">{{ c.servicesCount }} Services</td>
                <td class="p-3.5 font-mono text-slate-800 font-semibold">{{ c.apiCalls }} Calls</td>
                <td class="p-3.5 font-bold text-emerald-700">{{ c.uptime }}</td>
                <td class="p-3.5">
                  <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">Operational</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TAB 5: Institutional User Report -->
      <div *ngIf="activeTab() === 'inst-user'" class="space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table class="w-full text-left text-xs text-slate-700">
            <thead class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
              <tr>
                <th class="p-3.5">Institution Name</th>
                <th class="p-3.5">Admins</th>
                <th class="p-3.5">Data Officers</th>
                <th class="p-3.5">Auditors</th>
                <th class="p-3.5">Total User Accounts</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              <tr *ngFor="let iu of instUserData" class="hover:bg-emerald-50/30 transition">
                <td class="p-3.5 font-bold text-slate-900">{{ iu.institution }}</td>
                <td class="p-3.5 text-slate-800 font-bold">{{ iu.admins }}</td>
                <td class="p-3.5 text-slate-800 font-bold">{{ iu.officers }}</td>
                <td class="p-3.5 text-slate-800 font-bold">{{ iu.auditors }}</td>
                <td class="p-3.5 text-emerald-800 font-extrabold">{{ iu.total }} Users</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TAB 6: Institutional Services Report -->
      <div *ngIf="activeTab() === 'inst-services'" class="space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table class="w-full text-left text-xs text-slate-700">
            <thead class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
              <tr>
                <th class="p-3.5">Service Name</th>
                <th class="p-3.5">Owning Institution</th>
                <th class="p-3.5">Endpoint URL</th>
                <th class="p-3.5">Auth Type</th>
                <th class="p-3.5">Security Level</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              <tr *ngFor="let is of instServicesData" class="hover:bg-emerald-50/30 transition">
                <td class="p-3.5 font-bold text-slate-900">{{ is.serviceName }}</td>
                <td class="p-3.5 text-emerald-800 font-bold">{{ is.institution }}</td>
                <td class="p-3.5 font-mono text-[11px] text-slate-600">{{ is.endpoint }}</td>
                <td class="p-3.5"><span class="px-2 py-0.5 rounded bg-slate-100 text-slate-800 font-bold border border-slate-200">{{ is.auth }}</span></td>
                <td class="p-3.5"><span class="px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-extrabold">{{ is.secLevel }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TAB 7: Institutional Admin Data Sharing -->
      <div *ngIf="activeTab() === 'inst-admin-ds'" class="space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table class="w-full text-left text-xs text-slate-700">
            <thead class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
              <tr>
                <th class="p-3.5">Institutional Admin</th>
                <th class="p-3.5">Ministry / Agency</th>
                <th class="p-3.5">Incoming Approved</th>
                <th class="p-3.5">Outgoing Active</th>
                <th class="p-3.5">Compliance Score</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              <tr *ngFor="let ia of instAdminDsData" class="hover:bg-emerald-50/30 transition">
                <td class="p-3.5 font-bold text-slate-900">{{ ia.adminName }}</td>
                <td class="p-3.5 text-slate-700">{{ ia.agency }}</td>
                <td class="p-3.5 text-emerald-700 font-bold">{{ ia.incoming }} Agreements</td>
                <td class="p-3.5 text-teal-700 font-bold">{{ ia.outgoing }} Active</td>
                <td class="p-3.5">
                  <span class="px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-emerald-50 text-emerald-800 border border-emerald-200">
                    {{ ia.complianceScore }}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `
})
export class ReportsComponent {
  activeTab = signal<ReportTab>('account');
  selectedInstitution = 'all';
  startDate = '2026-01-01';
  endDate = '2026-07-23';

  tabs: { id: ReportTab; label: string; icon: string }[] = [
    { id: 'account', label: 'Account Report', icon: '👤' },
    { id: 'ds-general', label: 'Data Sharing (General)', icon: '📊' },
    { id: 'ds-specific', label: 'Data Sharing (Specific)', icon: '🔍' },
    { id: 'custodian-services', label: 'Custodian Services', icon: '⚡' },
    { id: 'inst-user', label: 'Institutional Users', icon: '🏢' },
    { id: 'inst-services', label: 'Institutional Services', icon: '🔌' },
    { id: 'inst-admin-ds', label: 'Institutional Admin DSA', icon: '🛡️' }
  ];

  accountReportData = [
    { name: 'Khamis Ali', email: 'k.ali@mofez.go.tz', institution: 'Ministry of Finance (MoFEZ)', role: 'Institutional Admin', lastActive: '10 mins ago' },
    { name: 'Fatma Said', email: 'f.said@mohz.go.tz', institution: 'Ministry of Health (MoHZ)', role: 'Data Protection Officer', lastActive: '1 hour ago' },
    { name: 'Salum Juma', email: 's.juma@zra.go.tz', institution: 'Zanzibar Revenue Authority', role: 'Institutional Admin', lastActive: '2 days ago' },
    { name: 'Amina Rashid', email: 'a.rashid@zanid.go.tz', institution: 'ZanID Registration Agency', role: 'Data Officer', lastActive: '5 mins ago' }
  ];

  dsGeneralData = [
    { category: 'Taxation & Revenues', consumer: 'Zanzibar Revenue Authority', provider: 'Ministry of Finance', date: '2026-06-12', status: 'Approved' },
    { category: 'Health & Social Care', consumer: 'Ministry of Health', provider: 'ZanID Registration Agency', date: '2026-07-02', status: 'Approved' },
    { category: 'Education & Grants', consumer: 'Ministry of Education', provider: 'Ministry of Finance', date: '2026-07-18', status: 'Under Review' },
    { category: 'Land & Titling', consumer: 'Zanzibar Investment Promotion', provider: 'Ministry of Lands', date: '2026-05-20', status: 'Approved' }
  ];

  dsSpecificData = [
    { ref: 'DSA-ZAN-2026-001', service: 'ZanID Citizen Verification API', fields: ['NIN', 'FullName', 'DOB', 'BiometricHash'], legalBasis: 'e-Gov Data Protection Act Sec. 14', expiry: '2027-06-12' },
    { ref: 'DSA-ZAN-2026-004', service: 'ZRA Tax Compliance Ledger', fields: ['TIN', 'TaxpayerStatus', 'FilingYear'], legalBasis: 'Revenue Code Regulation 8', expiry: '2028-01-01' }
  ];

  custodianServicesData = [
    { custodian: 'ZanID Agency', servicesCount: 4, apiCalls: '1,420,500', uptime: '99.98%' },
    { custodian: 'Ministry of Finance', servicesCount: 8, apiCalls: '3,890,120', uptime: '99.95%' },
    { custodian: 'Zanzibar Revenue Authority', servicesCount: 6, apiCalls: '2,100,450', uptime: '100.0%' }
  ];

  instUserData = [
    { institution: 'Ministry of Finance (MoFEZ)', admins: 4, officers: 18, auditors: 3, total: 25 },
    { institution: 'Ministry of Health (MoHZ)', admins: 3, officers: 12, auditors: 2, total: 17 },
    { institution: 'Zanzibar Revenue Authority', admins: 5, officers: 24, auditors: 4, total: 33 }
  ];

  instServicesData = [
    { serviceName: 'Citizen Verification Service', institution: 'ZanID Agency', endpoint: 'https://api.zanid.go.tz/v1/verify', auth: 'mTLS + OAuth2', secLevel: 'RESTRICTED' },
    { serviceName: 'Tax Clearance Validation', institution: 'ZRA', endpoint: 'https://api.zra.go.tz/v2/tax-clearance', auth: 'OAuth2 Token', secLevel: 'CONFIDENTIAL' }
  ];

  instAdminDsData = [
    { adminName: 'Khamis Ali', agency: 'Ministry of Finance (MoFEZ)', incoming: 12, outgoing: 8, complianceScore: 98 },
    { adminName: 'Salum Juma', agency: 'Zanzibar Revenue Authority', incoming: 15, outgoing: 11, complianceScore: 100 }
  ];

  getFilteredCount(): number {
    return 24;
  }

  exportReport(type: 'csv' | 'pdf') {
    alert(`Exporting active report (${this.activeTab()}) as ${type.toUpperCase()} file...`);
  }
}
